import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import { fetchAiUsageCostOverview } from '../../../services/systemAiUsageCosts';
import './AiUsageCostsPage.css';

const normalizeRoleName = (rawRole) => {
  const raw = (rawRole || '').trim();
  if (!raw) return '';
  const canonical = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (canonical === 'SYSADMIN') return 'SYS_ADMIN';
  if (canonical === 'TENANTADMIN') return 'TENANT_ADMIN';
  if (canonical === 'TENANTUSER') return 'TENANT_USER';
  return raw.toUpperCase();
};

const toDateInput = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 10);
};

const UI_TEXT = {
  'tr-TR': {
    pageTitle: 'AI Kullanimlari ve Maliyetleri',
    pageDescription: 'TENANT_ADMIN ve kullanici bazinda AI rapor kullanim adetleri ile maliyetleri izler. Gelecek urun paket yapisi icin AI dahil/haric ve limit bilgisi altyapisini da barindirir.',
    accessDenied: 'Bu modula sadece SYS_ADMIN rolundeki kullanicilar erisebilir.',
    loading: 'Yukleniyor...',
    refresh: 'Yenile',
    fromDate: 'Baslangic',
    toDate: 'Bitis',
    tenantAdmin: 'TENANT_ADMIN',
    allTenantAdmins: 'Tum tenant adminler',
    tenantId: 'Tenant ID',
    totalReports: 'Toplam Rapor',
    totalCost: 'Toplam Maliyet (USD)',
    totalTokens: 'Toplam Token',
    packageDefault: 'Paket Varsayimi',
    aiIncluded: 'AI Dahil',
    aiExcluded: 'AI Haric',
    packagePrepTitle: 'Urun Paketi Hazirlik Bilgisi',
    unlimited: 'sinirsiz',
    policyText: 'Varsayilan politika: tenant limiti __TENANT_LIMIT__, kullanici limiti __USER_LIMIT__, limit asiminda __LIMIT_MODE__ uygulanir, overage bedeli $__OVERAGE__ / rapor.',
    hardStop: 'hard-stop',
    overage: 'overage',
    adminSummaryTitle: 'TENANT_ADMIN Ozeti',
    userBreakdownTitle: 'Kullanici Kirilimi',
    report: 'Rapor',
    totalCostShort: 'Toplam Maliyet',
    userCount: 'Kullanici Sayisi',
    user: 'Kullanici',
    noRecords: 'Kayit bulunmuyor.',
    usageLoadError: 'AI kullanim verileri yuklenemedi.'
  },
  'en-US': {
    pageTitle: 'AI Usage and Costs',
    pageDescription: 'Tracks AI report usage counts and costs by TENANT_ADMIN and user. Also includes infrastructure for future package model options like AI included/excluded and limit settings.',
    accessDenied: 'Only users with SYS_ADMIN role can access this module.',
    loading: 'Loading...',
    refresh: 'Refresh',
    fromDate: 'Start',
    toDate: 'End',
    tenantAdmin: 'TENANT_ADMIN',
    allTenantAdmins: 'All tenant admins',
    tenantId: 'Tenant ID',
    totalReports: 'Total Reports',
    totalCost: 'Total Cost (USD)',
    totalTokens: 'Total Tokens',
    packageDefault: 'Package Default',
    aiIncluded: 'AI Included',
    aiExcluded: 'AI Excluded',
    packagePrepTitle: 'Package Preparation Info',
    unlimited: 'unlimited',
    policyText: 'Default policy: tenant limit __TENANT_LIMIT__, user limit __USER_LIMIT__, __LIMIT_MODE__ is applied on over-limit, overage fee is $__OVERAGE__ / report.',
    hardStop: 'hard-stop',
    overage: 'overage',
    adminSummaryTitle: 'TENANT_ADMIN Summary',
    userBreakdownTitle: 'User Breakdown',
    report: 'Report',
    totalCostShort: 'Total Cost',
    userCount: 'User Count',
    user: 'User',
    noRecords: 'No records found.',
    usageLoadError: 'AI usage data could not be loaded.'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const formatUsd = (value) => Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 });
const formatNum = (value, locale) => Number(value || 0).toLocaleString(locale);

function AiUsageCostsPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);
  const isSysAdmin = normalizeRoleName(sessionStorage.getItem('authRole')) === 'SYS_ADMIN';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [filters, setFilters] = useState(() => {
    const now = new Date();
    return {
      fromDate: toDateInput(new Date(now.getFullYear(), now.getMonth(), 1)),
      toDate: toDateInput(now),
      tenantAdminUserId: '',
      tenantId: ''
    };
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAiUsageCostOverview({
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        tenantAdminUserId: filters.tenantAdminUserId ? Number(filters.tenantAdminUserId) : undefined,
        tenantId: filters.tenantId ? Number(filters.tenantId) : undefined
      });
      setOverview(data);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('usageLoadError'));
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    if (isSysAdmin) load();
  }, [isSysAdmin, load]);

  const tenantAdminOptions = useMemo(() => {
    const rows = overview?.tenantAdminSummaries || [];
    return rows
      .map((item) => ({
        value: String(item.tenantAdminUserId || ''),
        label: `${item.tenantAdminUsername || '-'} (${item.tenantAdminFullName || '-'})`
      }))
      .filter((item) => item.value)
      .filter((item, index, array) => array.findIndex((row) => row.value === item.value) === index);
  }, [overview]);

  const policyText = useMemo(() => {
    const tenantLimit = overview?.packagePolicyDefaults?.monthlyReportLimitPerTenant ?? t('unlimited');
    const userLimit = overview?.packagePolicyDefaults?.monthlyReportLimitPerUser ?? t('unlimited');
    const limitMode = overview?.packagePolicyDefaults?.hardStopOnLimit ? t('hardStop') : t('overage');
    const overage = formatUsd(overview?.packagePolicyDefaults?.overageCostUsdPerReport || 0);

    return t('policyText')
      .replace('__TENANT_LIMIT__', String(tenantLimit))
      .replace('__USER_LIMIT__', String(userLimit))
      .replace('__LIMIT_MODE__', String(limitMode))
      .replace('__OVERAGE__', String(overage));
  }, [overview, t]);

  if (!isSysAdmin) {
    return (
      <div className="card ai-usage-costs-card">
        <PageHeader title={t('pageTitle')} description={t('accessDenied')} />
      </div>
    );
  }

  return (
    <div className="card ai-usage-costs-card">
      <PageHeader
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={<button type="button" className="btn-secondary" onClick={load} disabled={loading}>{loading ? t('loading') : t('refresh')}</button>}
      />

      <section className="ai-usage-filters">
        <label htmlFor="ai-usage-from-date">{t('fromDate')}
          <input id="ai-usage-from-date" type="date" value={filters.fromDate} onChange={(event) => setFilters((prev) => ({ ...prev, fromDate: event.target.value }))} />
        </label>
        <label htmlFor="ai-usage-to-date">{t('toDate')}
          <input id="ai-usage-to-date" type="date" value={filters.toDate} onChange={(event) => setFilters((prev) => ({ ...prev, toDate: event.target.value }))} />
        </label>
        <label htmlFor="ai-usage-tenant-admin">{t('tenantAdmin')}
          <select id="ai-usage-tenant-admin" value={filters.tenantAdminUserId} onChange={(event) => setFilters((prev) => ({ ...prev, tenantAdminUserId: event.target.value }))}>
            <option value="">{t('allTenantAdmins')}</option>
            {tenantAdminOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </label>
        <label htmlFor="ai-usage-tenant-id">{t('tenantId')}
          <input id="ai-usage-tenant-id" type="number" min="1" value={filters.tenantId} onChange={(event) => setFilters((prev) => ({ ...prev, tenantId: event.target.value }))} />
        </label>
      </section>

      {error ? <div className="alert alert-error">{error}</div> : null}

      <section className="ai-usage-kpis">
        <article><span>{t('totalReports')}</span><strong>{formatNum(overview?.totalReportCount || 0, localeKey)}</strong></article>
        <article><span>{t('totalCost')}</span><strong>${formatUsd(overview?.totalCostUsd || 0)}</strong></article>
        <article><span>{t('totalTokens')}</span><strong>{formatNum(overview?.totalTokens || 0, localeKey)}</strong></article>
        <article>
          <span>{t('packageDefault')}</span>
          <strong>{overview?.packagePolicyDefaults?.packageCode || 'UNSPECIFIED'}</strong>
          <small>{overview?.packagePolicyDefaults?.aiIncluded ? t('aiIncluded') : t('aiExcluded')}</small>
        </article>
      </section>

      <section className="ai-usage-package-note">
        <h3>{t('packagePrepTitle')}</h3>
        <p>{policyText}</p>
      </section>

      <section className="ai-usage-table-panel">
        <h3>{t('adminSummaryTitle')}</h3>
        <div className="table-wrapper">
          <table className="ai-usage-table">
            <thead>
              <tr>
                <th>{t('tenantId')}</th>
                <th>{t('tenantAdmin')}</th>
                <th>{t('report')}</th>
                <th>{t('totalCostShort')}</th>
                <th>{t('totalTokens')}</th>
                <th>{t('userCount')}</th>
              </tr>
            </thead>
            <tbody>
              {(overview?.tenantAdminSummaries || []).length === 0 ? (
                <tr><td colSpan={6} className="table-placeholder">{loading ? t('loading') : t('noRecords')}</td></tr>
              ) : (
                (overview?.tenantAdminSummaries || []).map((item) => (
                  <tr key={`${item.tenantId}-${item.tenantAdminUserId}`}>
                    <td>{item.tenantId || '-'}</td>
                    <td>{item.tenantAdminUsername} ({item.tenantAdminFullName})</td>
                    <td>{formatNum(item.reportCount, localeKey)}</td>
                    <td>${formatUsd(item.totalCostUsd)}</td>
                    <td>{formatNum(item.totalTokens, localeKey)}</td>
                    <td>{formatNum(item.uniqueUserCount, localeKey)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="ai-usage-table-panel">
        <h3>{t('userBreakdownTitle')}</h3>
        <div className="table-wrapper">
          <table className="ai-usage-table">
            <thead>
              <tr>
                <th>{t('tenantId')}</th>
                <th>{t('tenantAdmin')}</th>
                <th>{t('user')}</th>
                <th>{t('report')}</th>
                <th>{t('totalCostShort')}</th>
                <th>{t('totalTokens')}</th>
              </tr>
            </thead>
            <tbody>
              {(overview?.userBreakdowns || []).length === 0 ? (
                <tr><td colSpan={6} className="table-placeholder">{loading ? t('loading') : t('noRecords')}</td></tr>
              ) : (
                (overview?.userBreakdowns || []).map((item) => (
                  <tr key={`${item.tenantId}-${item.tenantAdminUserId}-${item.userId}`}>
                    <td>{item.tenantId || '-'}</td>
                    <td>{item.tenantAdminUsername}</td>
                    <td>{item.username} ({item.fullName})</td>
                    <td>{formatNum(item.reportCount, localeKey)}</td>
                    <td>${formatUsd(item.totalCostUsd)}</td>
                    <td>{formatNum(item.totalTokens, localeKey)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default AiUsageCostsPage;
